import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { ButtonSubmit, FormAlert } from 'components';
import { updatePasswordSchema } from './updatePasswordSchema';
import { updatePasswordValidation } from './updatePasswordValidation';
import { useAppDispatch, useAppSelector } from 'lib/redux';
import { selectMe, passwordReset } from '../accountSlice';
import {
  Box,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup, InputLeftElement,
  Link,
  Stack,
  Text
} from "@chakra-ui/react";
import { Field, FieldProps, Formik, Form } from "formik";
import { v4 as uuid } from "uuid";
import { getIcon } from "lib/icons";
import { generateKeys, sha512, useE2EEContext } from 'lib/e2ee';

type State = {
  error: string;
  success: string;
}

const DEFAULT_ERROR = 'There was an error, please try again';

const initialState = { error: '', success: '' };

export const UpdatePasswordForm: FC<any> = () => {
  const { user } = useAppSelector(selectMe);
  const [ state, setState ] = useState<State>(initialState);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { createNonce } = useE2EEContext();

  const onSubmit = async (password: string) => {
    try {
      setState({ ...initialState });

      const userId = { email: user.email, name: `${user.first_name} ${user.last_name}`};
      const { privateKeyArmored: private_key_armored, publicKeyArmored: public_key_armored } = await generateKeys(userId, password);

      const updatedUser = await dispatch(passwordReset({ password: sha512(password), private_key_armored, public_key_armored })).unwrap();

      await createNonce(updatedUser, password);

      setState({ ...initialState, success: 'Your password has been updated.' });
      router.push('/login'); // Redirect to the login page. That way, the logic to redirect to either the select team page or the correct team will happen.
    } catch (e) {
      setState({ ...state, error: DEFAULT_ERROR });
    }
  }

  return (
    <Formik
      initialValues={{ password: '' }}
      enableReinitialize={true}
      validationSchema={updatePasswordValidation}
      onSubmit={(values) => onSubmit(values.password)}
    >
      <Form>
        <Stack spacing={6} width="100%">
          <FormAlert success={state.success} error={state.error} />
          {updatePasswordSchema.map((schema) => (
            <Field key={uuid()} name={schema.name}>
              {({field, form, meta}: FieldProps) => (
                <FormControl
                  isInvalid={Boolean(meta.touched && form.errors[field.name])}
                  isRequired={true}
                >
                  <InputGroup size="md">
                    <InputLeftElement height="100%" width="30px" ml="15px">
                      {getIcon(schema.icon)}
                    </InputLeftElement>
                    <Input pr="2rem" pl="4rem" size="lg"{...schema} {...field} />
                  </InputGroup>
                  <FormErrorMessage>{form.errors[field.name]}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          ))}
          <Box width="100%" textAlign="center">
            <Text size="sm">
              By clicking the button below, you indicate that you agree to the <Link href='/privacy_policy.pdf' isExternal>privacy policy</Link> and <Link href="/terms_and_conditions.pdf" isExternal>terms of service</Link>. This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a href="https://policies.google.com/terms" rel="noreferrer" target="_blank">Terms of Service</a> apply.
            </Text>
          </Box>
          <ButtonSubmit isFullWidth={true} size="xl">Update Password</ButtonSubmit>
        </Stack>
      </Form>
    </Formik>
  )
}
